import { useLocation, useNavigate } from "react-router-dom";
import { Activity, House, LayoutGrid, Plug, Smartphone, Sparkles, User } from "lucide-react";
import { cn } from "@/lib/cn";
import { useWorkspace } from "@/app/WorkspaceContext";
import { useAskNara } from "@/app/AskNara";
import type { PropertyKind } from "@/data/hooks";
import { NaraOrb } from "./NaraOrb";

interface Slot {
  key: string;
  path: string;
  label: string;
  icon: typeof House;
  fab?: boolean;
  /** open Ask Nara instead of navigating (apps can't build pages) */
  ask?: boolean;
}

/**
 * Per-kind navigation (the design decision): the tab SET adapts to the selected
 * property's kind. Built sites get Make+Library; connected sites swap Library
 * for Install; mobile apps drop Make/Library entirely (the center action becomes
 * Ask Nara, since you can't build native screens here).
 */
const NAV: Record<PropertyKind, Slot[]> = {
  built: [
    { key: "home", path: "/home", label: "Home", icon: House },
    { key: "signals", path: "/signals", label: "Signals", icon: Activity },
    { key: "make", path: "/make", label: "Make", icon: Sparkles, fab: true },
    { key: "library", path: "/library", label: "Library", icon: LayoutGrid },
    { key: "you", path: "/you", label: "You", icon: User },
  ],
  connected: [
    { key: "home", path: "/home", label: "Home", icon: House },
    { key: "signals", path: "/signals", label: "Signals", icon: Activity },
    { key: "make", path: "/make", label: "Make", icon: Sparkles, fab: true },
    { key: "install", path: "/you/integrations", label: "Install", icon: Plug },
    { key: "you", path: "/you", label: "You", icon: User },
  ],
  app: [
    { key: "home", path: "/home", label: "Home", icon: House },
    { key: "signals", path: "/signals", label: "Signals", icon: Activity },
    { key: "nara", path: "/make", label: "Nara", icon: Sparkles, fab: true, ask: true },
    { key: "install", path: "/sdk", label: "SDK", icon: Smartphone },
    { key: "you", path: "/you", label: "You", icon: User },
  ],
};

export function LiquidGlassNav() {
  const loc = useLocation();
  const nav = useNavigate();
  const { current } = useWorkspace();
  const { setOpen } = useAskNara();

  const slots = NAV[current?.propertyKind ?? "built"];

  const homeActive = loc.pathname.startsWith("/home");

  const go = (s: Slot) => {
    if (s.ask) setOpen(true);
    else nav(s.path);
  };

  return (
    <nav
      aria-label="Primary"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center"
      style={{ paddingBottom: "max(env(safe-area-inset-bottom), 14px)" }}
    >
      <div
        className={cn(
          "glass glass--matte pointer-events-auto relative mx-4 flex w-full max-w-[420px] items-stretch rounded-full",
          "h-16 md:h-[72px] md:max-w-[560px]",
        )}
      >
        {slots.map((slot) => {
          const Icon = slot.icon;
          const active = slot.path === "/home" ? homeActive : loc.pathname.startsWith(slot.path);
          if (slot.fab) {
            return (
              <div key={slot.key} className="flex flex-1 items-center justify-center">
                <button
                  type="button"
                  // The center orb IS the ⌘K command palette: pressing it opens
                  // Ask Nara on every property kind (not a plain nav to /make), so
                  // the signature action is one consistent "summon Nara" gesture.
                  onClick={() => setOpen(true)}
                  aria-label="Ask Nara (⌘K)"
                  className={cn(
                    // Seated in the MIDDLE of the bar with only a slight lift —
                    // the signature Nara action: the crimson liquid-glass orb.
                    "group relative -translate-y-1.5 grid place-items-center rounded-full",
                    "h-[52px] w-[52px] md:h-[60px] md:w-[60px] shadow-[0_8px_22px_-8px_rgba(174,52,42,0.75)]",
                    "border-[3px] border-canvas transition-transform duration-200 ease-out-soft",
                    "hover:-translate-y-2.5 active:-translate-y-0.5",
                  )}
                >
                  {active && <span className="absolute inset-0 rounded-full bg-accent/40 animate-pulse-ring" aria-hidden />}
                  {/* the orb fills the button. A guaranteed inline dark-crimson disc
                      sits underneath: it's the right dark backdrop for the liquid-glass
                      orb in real browsers, and an on-brand crimson orb anywhere WebGL
                      can't paint (e.g. headless) — never a blank/white button. */}
                  <span
                    className="absolute inset-0 overflow-hidden rounded-full"
                    style={{
                      background:
                        "radial-gradient(circle at 50% 38%, #1a0a0e 0%, #0a0510 60%, #05030a 100%)",
                    }}
                  >
                    <NaraOrb />
                  </span>
                </button>
              </div>
            );
          }
          return (
            <button
              key={slot.key}
              type="button"
              onClick={() => go(slot)}
              aria-current={active ? "page" : undefined}
              className="group flex flex-1 items-center justify-center"
            >
              <span
                className={cn(
                  "flex flex-col items-center justify-center gap-1 rounded-2xl px-3 py-1.5 transition-all duration-200",
                  active
                    ? "bg-accent-soft text-ink-strong dark:bg-white/10"
                    : "text-muted group-hover:text-ink",
                )}
              >
                <Icon
                  className="h-[22px] w-[22px]"
                  strokeWidth={active ? 2 : 1.6}
                  fill={slot.key === "you" && active ? "currentColor" : "none"}
                />
                <span className={cn("font-mono text-[10px] leading-none tracking-wide", active ? "opacity-100" : "opacity-70")}>
                  {slot.label}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
